"use client";
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { useRouter, usePathname } from "next/navigation";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight } from "lucide-react";
import Image from "next/image";

const transition = {
  type: "spring",
  mass: 0.5,
  damping: 11.5,
  stiffness: 100,
  restDelta: 0.001,
  restSpeed: 0.001,
};

export const MenuItem = ({
  setActive,
  active,
  item,
  children,
  className,
}: {
  setActive: (item: string) => void;
  active: string | null;
  item: string;
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div onMouseEnter={() => setActive(item)} className="relative">
      <motion.p
        transition={{ duration: 0.3 }}
        className={cn(
          "cursor-pointer text-base font-medium text-white/90 hover:text-white relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-white after:transition-all hover:after:w-full",
          className
        )}
      >
        {item}
      </motion.p>
      {active !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={transition}
        >
          {active === item && (
            <div className="absolute top-[calc(100%_+_1.2rem)] left-1/2 transform -translate-x-1/2 pt-4">
              <motion.div
                transition={transition}
                layoutId="active"
                className="bg-slate-950/95 backdrop-blur-sm rounded-xl overflow-hidden border border-[rgb(var(--color-neon-blue)_/_0.1)] shadow-[0_8px_32px_rgba(0,0,0,0.4),_0_0_20px_rgba(var(--color-neon-blue),0.1)]"
              >
                <motion.div layout className="w-max h-full p-4">
                  {children}
                </motion.div>
              </motion.div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export const Menu = ({
  setActive,
  children,
}: {
  setActive: (item: string | null) => void;
  children: React.ReactNode;
}) => {
  const pathname = usePathname();

  useEffect(() => {
    setActive(null);
  }, [pathname, setActive]);

  return (
    <nav
      onMouseLeave={() => setActive(null)}
      className="relative flex justify-center space-x-8 px-8 py-4"
    >
      {children}
    </nav>
  );
};

export const ServiceItem = ({
  title,
  description,
  href,
  icon: Icon,
  className,
}: {
  title: string;
  description: string;
  href: string;
  icon: React.ElementType;
  className?: string;
}) => {
  return (
    <Link href={href} className={cn("group", className)}>
      <Card className="relative h-full bg-slate-950/50 backdrop-blur-xl rounded-xl border border-[rgb(var(--color-neon-blue)_/_0.1)] shadow-[0_8px_16px_rgba(0,0,0,0.2),_0_0_20px_rgba(var(--color-neon-blue),0.1)] hover:bg-slate-900/50 transition-colors">
        <CardContent className="p-5 flex flex-col h-full">
          <div className="mb-3 rounded-full bg-[rgb(var(--color-neon-blue)_/_0.1)] border border-[rgb(var(--color-neon-blue)_/_0.1)] p-2.5 w-fit">
            <Icon className="h-6 w-6 text-white/90" />
          </div>
          <h4 className="text-lg font-semibold text-white/90 mb-2">{title}</h4>
          <p className="text-base text-white/80 mb-4 leading-relaxed flex-1">
            {description}
          </p>
          <div className="inline-flex items-center text-base font-medium text-[rgb(var(--color-neon-blue-light))] group-hover:text-[rgb(var(--color-neon-green))] transition-colors">
            Learn more
            <ChevronRight className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export const ProductItem = ({
  title,
  description,
  href,
  src,
}: {
  title: string;
  description: string;
  href: string;
  src: string;
}) => {
  return (
    <Link href={href} className="flex space-x-2">
      <Image
        src={src}
        width={140}
        height={70}
        alt={title}
        className="flex-shrink-0 rounded-md shadow-2xl"
      />
      <div>
        <h4 className="text-xl font-bold mb-1 text-black dark:text-white">
          {title}
        </h4>
        <p className="text-neutral-700 text-sm max-w-[10rem] dark:text-neutral-300">
          {description}
        </p>
      </div>
    </Link>
  );
};

export const HoveredLink = ({ children, className, ...rest }: any) => {
  return (
    <Link
      {...rest}
      className={cn(
        "text-base font-medium text-white/90 hover:text-white relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 after:bg-white after:transition-all hover:after:w-full",
        className
      )}
    >
      {children}
    </Link>
  );
};
